import { useEffect, useMemo, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { http, listProjects, listSyncs } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, ExternalLink, Code, Link2, X, FileCode, Pencil } from "lucide-react";

// Power BI's "Web URL" / web-content tile renders an iframe in a fixed-size
// container — typically a single dashboard tile at 1280×720 (full page) or
// half of it. We default to 100% width and a height that fits a half-page
// tile comfortably while still showing ~12 Gantt rows.
const DEFAULT_EMBED_WIDTH = "100%";
const DEFAULT_EMBED_HEIGHT = 720;

function buildEmbedSnippet(src, { width = DEFAULT_EMBED_WIDTH, height = DEFAULT_EMBED_HEIGHT } = {}) {
  // Power BI's iframe wrapper applies `sandbox="allow-scripts ..."` automatically;
  // including it explicitly keeps the snippet portable to other embeds and
  // documents that this is the supported sandbox set.
  return `<iframe src="${src}" width="${width}" height="${height}" frameborder="0" allowfullscreen sandbox="allow-scripts allow-forms allow-popups" loading="lazy" title="Cornerstone Gantt"></iframe>`;
}

export default function ShareLinks() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [embedFor, setEmbedFor] = useState(null); // link being embedded
  const [editingLink, setEditingLink] = useState(null); // link being edited

  // Always derive URLs from the current window origin so that links automatically
  // reflect the deployed domain — even if the backend stored an old FRONTEND_URL.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const buildBrowser = (token) => `${origin}/share/${token}`;
  const buildIframe = (token) => `${origin}/share/${token}/iframe`;

  const refresh = async () => {
    setLoading(true);
    try { const r = await http.get("/share-links"); setLinks(r.data.links || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este link?")) return;
    try { await http.delete(`/share-links/${id}`); toast.success("Removido"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="px-8 lg:px-12 py-10 max-w-6xl" data-testid="share-links-page">
      <header className="flex items-end justify-between">
        <div>
          <div className="cs-label">distribute</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Compartilhamento</h1>
          <p className="text-sm text-zinc-600 mt-1">Gere links públicos do Gantt para clientes ou para embed no Power BI.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-cs-accent text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-cs-accentHover" data-testid="share-create-btn">
            <Plus size={16} /> Novo Link
          </button>
        )}
      </header>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-500">Carregando…</div>
      ) : links.length === 0 ? (
        <div className="mt-10 border border-dashed border-zinc-300 rounded-md p-12 text-center bg-white" data-testid="share-empty">
          <p className="text-sm text-zinc-500">Nenhum link criado ainda.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {links.map((l) => (
            <div key={l.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`share-row-${l.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-heading text-lg font-semibold flex items-center gap-2">
                    <span>{l.name}</span>
                    <ExpiryBadge expiresAt={l.expires_at} />
                  </div>
                  {l.description && <div className="text-xs text-zinc-500 mt-1">{l.description}</div>}
                  <div className="font-mono text-[11px] text-zinc-500 mt-2">
                    {l.include_all_projects ? (l.effective || []).length : l.projects.length} projeto{((l.include_all_projects ? (l.effective || []).length : l.projects.length) !== 1) ? "s" : ""} ·{" "}
                    <span data-testid={`share-version-count-${l.id}`}>
                      {(l.effective || []).reduce((acc, p) => acc + (p.syncs?.length || 0), 0)} versão(ões)
                    </span>{" "}
                    · {l.enabled ? "ativo" : "inativo"}
                    {l.projects.some((p) => p.include_all_versions) && (
                      <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cs-blueprint/10 text-cs-blueprint" data-testid={`share-link-${l.id}-all-badge`}>
                        {t("share_all_versions_badge")}
                      </span>
                    )}
                    {l.include_all_projects && (
                      <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900 text-white" data-testid={`share-link-${l.id}-all-projects-badge`}>
                        {t("share_all_projects_badge")}
                      </span>
                    )}
                    {l.projects.some((p) => p.track_latest) && (
                      <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cs-accent/10 text-cs-accent" data-testid={`share-link-${l.id}-live-badge`}>
                        ⚡ {t("share_live_badge")}
                      </span>
                    )}
                    {l.expires_at && <> · expira {new Date(l.expires_at).toLocaleString()}</>}
                  </div>
                  <EffectiveSummary link={l} />
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditingLink(l)} className="p-1.5 text-zinc-400 hover:text-cs-accent hover:bg-zinc-100 rounded" title="Editar link" data-testid={`share-edit-${l.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => onDelete(l.id)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-zinc-100 rounded">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-4 border-t border-zinc-100">
                <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="inline-flex items-center gap-1.5 cs-label !text-[10px]"><Link2 size={11} /> Navegador</div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => copyText(buildBrowser(l.token))} className="p-1 text-zinc-500 hover:text-cs-accent" data-testid={`share-copy-browser-${l.id}`}><Copy size={11} /></button>
                      <a href={buildBrowser(l.token)} target="_blank" rel="noreferrer" className="p-1 text-zinc-500 hover:text-cs-accent"><ExternalLink size={11} /></a>
                    </div>
                  </div>
                  <code className="block text-[10px] font-mono break-all text-zinc-700">{buildBrowser(l.token)}</code>
                </div>
                <div className={`border rounded-md p-3 ${l.allow_iframe ? "border-zinc-200 bg-zinc-50/50" : "border-dashed border-zinc-200 bg-white opacity-50"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="inline-flex items-center gap-1.5 cs-label !text-[10px]"><Code size={11} /> Power BI iframe</div>
                    {l.allow_iframe && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEmbedFor(l)}
                          className="p-1 text-zinc-500 hover:text-cs-accent inline-flex items-center gap-1 text-[10px]"
                          title="Gerar snippet HTML para colar no Power BI"
                          data-testid={`share-embed-${l.id}`}
                        >
                          <FileCode size={11} /> embed
                        </button>
                        <button onClick={() => copyText(buildIframe(l.token))} className="p-1 text-zinc-500 hover:text-cs-accent" data-testid={`share-copy-iframe-${l.id}`}><Copy size={11} /></button>
                      </div>
                    )}
                  </div>
                  <code className="block text-[10px] font-mono break-all text-zinc-700">{l.allow_iframe ? buildIframe(l.token) : "Embed desativado"}</code>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <ShareLinkFormModal onClose={() => setCreating(false)} onSaved={refresh} />}
      {editingLink && (
        <ShareLinkFormModal
          existing={editingLink}
          onClose={() => setEditingLink(null)}
          onSaved={refresh}
        />
      )}
      {embedFor && (
        <EmbedSnippetModal
          link={embedFor}
          iframeUrl={buildIframe(embedFor.token)}
          onClose={() => setEmbedFor(null)}
        />
      )}
    </div>
  );
}

/**
 * EffectiveSummary — what the PUBLIC endpoints serve right now.
 *
 * The stored configuration and the served payload diverge as soon as the
 * "live" modes are used (always-latest, all-versions), so the admin list
 * shows the resolved versions returned by the backend instead of just the
 * pinned ids.
 */
function EffectiveSummary({ link }) {
  const { t } = useI18n();
  const eff = link.effective || [];
  if (!eff.length) {
    return <div className="text-[11px] font-mono text-amber-700 mt-2" data-testid={`share-effective-empty-${link.id}`}>{t("share_effective_empty")}</div>;
  }
  return (
    <div className="mt-2.5 space-y-1" data-testid={`share-effective-${link.id}`}>
      <div className="cs-label !text-[10px]">{t("share_effective_label")}</div>
      {eff.map((p) => (
        <div key={p.project_id} className="text-[11px] font-mono text-zinc-600 flex items-center gap-1.5 flex-wrap">
          <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.project_color || "#F97316" }} />
          <span className="font-semibold text-zinc-800">{p.project_name || "—"}</span>
          {p.include_all_versions && (
            <span className="px-1.5 rounded bg-cs-blueprint/10 text-cs-blueprint">{t("share_all_versions_badge")}</span>
          )}
          {p.track_latest && (
            <span className="px-1.5 rounded bg-cs-accent/10 text-cs-accent">⚡ {t("share_live_badge")}</span>
          )}
          <span data-testid={`share-effective-versions-${link.id}-${p.project_id}`}>
            {p.syncs.map((s) => `v${s.version}`).join(", ") || "—"}
          </span>
          <span className="text-zinc-400">({p.syncs.length})</span>
        </div>
      ))}
    </div>
  );
}

function ExpiryBadge({ expiresAt }) {
  if (!expiresAt) return null;
  const exp = new Date(expiresAt);
  if (isNaN(exp.getTime())) return null;
  const now = new Date();
  const diffMs = exp.getTime() - now.getTime();
  if (diffMs <= 0) {
    return <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid="share-badge-expired">expirado</span>;
  }
  const diffH = diffMs / 3600000;
  if (diffH < 24) {
    return <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="share-badge-soon">expira em {Math.max(1, Math.round(diffH))}h</span>;
  }
  const diffD = Math.round(diffH / 24);
  return <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600" data-testid="share-badge-future">expira em {diffD}d</span>;
}

function EmbedSnippetModal({ link, iframeUrl, onClose }) {
  const [width, setWidth] = useState(DEFAULT_EMBED_WIDTH);
  const [height, setHeight] = useState(DEFAULT_EMBED_HEIGHT);
  const [preset, setPreset] = useState("powerbi-full");

  const PRESETS = [
    { id: "powerbi-full", label: "Power BI — página inteira (1280×720)", w: "100%", h: 720 },
    { id: "powerbi-tile", label: "Power BI — tile metade (1280×360)", w: "100%", h: 360 },
    { id: "powerbi-card", label: "Power BI — card pequeno (640×400)", w: "100%", h: 400 },
    { id: "intranet", label: "Intranet / iframe livre (responsivo)", w: "100%", h: 800 },
    { id: "custom", label: "Personalizado", w: null, h: null },
  ];

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p?.w != null) setWidth(p.w);
    if (p?.h != null) setHeight(p.h);
  };

  const snippet = buildEmbedSnippet(iframeUrl, { width, height });
  const copy = () => {
    navigator.clipboard.writeText(snippet);
    toast.success("Snippet copiado — cole no Power BI (Custom Visual 'HTML Content' ou na opção 'Web URL').");
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/50 z-50 flex items-center justify-center p-4" data-testid="embed-modal">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="cs-label">Power BI embed</div>
            <h2 className="font-heading text-xl font-semibold mt-1">{link.name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100" data-testid="embed-close"><X size={16} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 scrollbar-thin space-y-4">
          <div>
            <label className="cs-label !text-[10px] mb-1.5 block">preset</label>
            <select
              value={preset}
              onChange={(e) => applyPreset(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm"
              data-testid="embed-preset-select"
            >
              {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="cs-label !text-[10px] mb-1.5 block">largura (px ou %)</label>
              <input
                type="text"
                value={width}
                onChange={(e) => { setWidth(e.target.value); setPreset("custom"); }}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono"
                data-testid="embed-width-input"
              />
            </div>
            <div>
              <label className="cs-label !text-[10px] mb-1.5 block">altura (px)</label>
              <input
                type="number"
                value={height}
                min={200}
                max={2400}
                onChange={(e) => { setHeight(parseInt(e.target.value, 10) || 720); setPreset("custom"); }}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono"
                data-testid="embed-height-input"
              />
            </div>
          </div>

          <div>
            <div className="cs-label !text-[10px] mb-1.5">snippet HTML</div>
            <pre
              className="bg-zinc-950 text-zinc-100 text-[11px] font-mono p-3 rounded-md whitespace-pre-wrap break-all leading-relaxed"
              data-testid="embed-snippet"
            >{snippet}</pre>
          </div>

          <div className="border border-zinc-200 rounded-md p-3 bg-white">
            <div className="cs-label !text-[10px] mb-1.5">pré-visualização</div>
            <div className="w-full overflow-hidden rounded border border-zinc-200 bg-zinc-50" style={{ height: Math.min(parseInt(height, 10) || 720, 360) }}>
              <iframe
                src={iframeUrl}
                title="Preview"
                className="w-full h-full"
                sandbox="allow-scripts allow-forms allow-popups"
                loading="lazy"
                data-testid="embed-preview-iframe"
              />
            </div>
            <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">⚠︎ Preview com altura limitada a 360px só pra caber no modal — sua altura real no Power BI será {height}px.</p>
          </div>

          <details className="text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded p-3">
            <summary className="font-semibold cursor-pointer">Como colar no Power BI</summary>
            <ol className="list-decimal pl-5 mt-2 space-y-1">
              <li>Power BI Desktop → <b>Insert → Web content</b>, cole o snippet HTML acima.</li>
              <li>Ou use o custom visual <b>HTML Content</b> (HTML Content by Daniel Marsh-Patrick) e cole o snippet no campo do visual.</li>
              <li>Em <b>Power BI Service</b>: tile do tipo <i>Web content</i> → cole o snippet.</li>
              <li>Se quiser embed em SharePoint/Intranet, qualquer iframe HTML standard funciona.</li>
            </ol>
          </details>
        </div>
        <div className="px-6 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="embed-cancel">Fechar</button>
          <button onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 bg-cs-accent text-white text-sm font-semibold rounded-md hover:bg-cs-accentHover" data-testid="embed-copy">
            <Copy size={14} /> Copiar snippet
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareLinkFormModal({ existing = null, onClose, onSaved }) {
  const { t } = useI18n();
  // When `existing` is passed we're EDITING — pre-fill all fields and PATCH on save.
  // Otherwise we're CREATING and POST a new link.
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [allowIframe, setAllowIframe] = useState(existing?.allow_iframe ?? true);
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  // ISO string ↔ `<input type="datetime-local">` value (YYYY-MM-DDThh:mm).
  // Backend stores ISO with timezone offset; here we strip TZ for the
  // local-time picker and re-apply the user's local TZ on save.
  const isoToLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [expiresAtInput, setExpiresAtInput] = useState(isoToLocalInput(existing?.expires_at));
  const [includeAllProjects, setIncludeAllProjects] = useState(!!existing?.include_all_projects);
  const [allProjectsAllVersions, setAllProjectsAllVersions] = useState(!!existing?.all_projects_include_all_versions);
  const [projects, setProjects] = useState([]);
  const [syncsByProject, setSyncsByProject] = useState({});
  const [rows, setRows] = useState(() => {
    if (!isEdit) {
      return [{ _uid: `srow_${Math.random().toString(36).slice(2, 10)}`, project_id: "", sync_ids: [], track_latest: false, include_all_versions: false }];
    }
    return (existing.projects || []).map((p) => ({
      _uid: `srow_${Math.random().toString(36).slice(2, 10)}`,
      project_id: p.project_id,
      sync_ids: p.sync_ids || [],
      track_latest: !!p.track_latest,
      include_all_versions: !!p.include_all_versions,
    }));
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const d = await listProjects();
      setProjects(d.projects || []);
      // Pre-load syncs for every project already selected in the link
      if (isEdit) {
        const projIds = Array.from(new Set((existing.projects || []).map((p) => p.project_id)));
        await Promise.all(projIds.map(async (pid) => {
          const s = await listSyncs(pid);
          setSyncsByProject((prev) => ({ ...prev, [pid]: s.syncs || [] }));
        }));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSyncs = async (pid) => {
    if (!pid || syncsByProject[pid]) return;
    const d = await listSyncs(pid);
    setSyncsByProject((p) => ({ ...p, [pid]: d.syncs || [] }));
  };

  // A super-admin can attach a project from another company to a link, and
  // `listProjects()` only returns the ACTIVE company's projects — the select
  // would then render the empty placeholder and the screen would not match
  // the saved configuration. Merge the names the backend already resolved.
  const projectOptions = useMemo(() => {
    const map = new Map(projects.map((p) => [p.id, p.name]));
    for (const e of existing?.effective || []) {
      if (e.project_id && !map.has(e.project_id)) map.set(e.project_id, e.project_name || e.project_id);
    }
    for (const r of rows) {
      if (r.project_id && !map.has(r.project_id)) map.set(r.project_id, r.project_id);
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [projects, existing, rows]);

  const removeRow = (i) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, idx) => idx !== i));
  };

  const onSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        allow_iframe: allowIframe,
        enabled,
        // Convert local datetime-input back to ISO with timezone so the
        // backend can compare against `datetime.now().astimezone()`.
        expires_at: expiresAtInput ? new Date(expiresAtInput).toISOString() : null,
        include_all_projects: includeAllProjects,
        all_projects_include_all_versions: allProjectsAllVersions,
        projects: rows
          .filter((r) => r.project_id && (r.sync_ids.length || r.track_latest || r.include_all_versions))
          .map((r) => ({
            project_id: r.project_id,
            sync_ids: r.sync_ids,
            track_latest: !!r.track_latest,
            include_all_versions: !!r.include_all_versions,
          })),
      };
      if (!payload.name) { toast.error("Informe um nome"); return; }
      if (!payload.projects.length && !includeAllProjects) { toast.error("Selecione ao menos um projeto+versão"); return; }
      if (isEdit) {
        await http.patch(`/share-links/${existing.id}`, payload);
        toast.success("Link atualizado — token mantido, URLs inalteradas");
      } else {
        await http.post("/share-links", payload);
        toast.success("Link criado");
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const addRow = () => setRows([...rows, { _uid: `srow_${Math.random().toString(36).slice(2, 10)}`, project_id: "", sync_ids: [], track_latest: false, include_all_versions: false }]);
  const setRow = (i, patch) => { const next = [...rows]; next[i] = { ...next[i], ...patch }; setRows(next); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" data-testid={isEdit ? "share-edit-modal" : "share-create-modal"}>
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div>
            {isEdit && <div className="cs-label">editar · token preservado</div>}
            <h3 className="font-heading text-base font-semibold mt-0.5">{isEdit ? `Editar “${existing.name}”` : "Novo Link de Compartilhamento"}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto p-5 flex-1 scrollbar-thin space-y-4">
          <div>
            <label className="cs-label block mb-1.5">Nome</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm" data-testid="share-name-input" />
          </div>
          <div>
            <label className="cs-label block mb-1.5">Descrição (opcional)</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm" />
          </div>
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/60">
            <label className="inline-flex items-start gap-1.5 text-xs text-zinc-800 cursor-pointer leading-tight">
              <input
                type="checkbox"
                checked={includeAllProjects}
                onChange={(e) => setIncludeAllProjects(e.target.checked)}
                className="mt-0.5 rounded text-cs-accent focus:ring-cs-accent"
                data-testid="share-all-projects"
              />
              <span>
                <span className="font-semibold">{t("share_all_projects_label")}</span>
                {" — "}
                <span className="text-zinc-500">{t("share_all_projects_help")}</span>
              </span>
            </label>
            {includeAllProjects && (
              <label className="mt-2 ml-5 inline-flex items-start gap-1.5 text-[11px] text-zinc-700 cursor-pointer leading-tight">
                <input
                  type="checkbox"
                  checked={allProjectsAllVersions}
                  onChange={(e) => setAllProjectsAllVersions(e.target.checked)}
                  className="mt-0.5 rounded text-cs-accent focus:ring-cs-accent"
                  data-testid="share-all-projects-all-versions"
                />
                <span>{t("share_all_projects_versions_label")}</span>
              </label>
            )}
          </div>
          <div>
            <label className="cs-label block mb-1.5">Projetos & Versões</label>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={r._uid} className="grid grid-cols-12 gap-2 items-start">
                  <select value={r.project_id} onChange={(e) => { setRow(i, { project_id: e.target.value, sync_ids: [] }); loadSyncs(e.target.value); }} className="col-span-5 px-2 py-1.5 border border-zinc-200 rounded-md text-xs" data-testid={`share-proj-${i}`}>
                    <option value="">Projeto…</option>
                    {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <div className="col-span-6 space-y-1.5">
                    <select multiple value={r.sync_ids} onChange={(e) => setRow(i, { sync_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} disabled={!r.project_id || r.include_all_versions} className="w-full px-2 py-1.5 border border-zinc-200 rounded-md text-xs font-mono disabled:bg-zinc-100 h-20" data-testid={`share-sync-${i}`}>
                      {(syncsByProject[r.project_id] || []).map((s) => <option key={s.id} value={s.id}>v{s.version} · {s.name}</option>)}
                    </select>
                    <label className="inline-flex items-start gap-1.5 text-[11px] text-zinc-700 cursor-pointer leading-tight">
                      <input
                        type="checkbox"
                        checked={!!r.include_all_versions}
                        onChange={(e) => setRow(i, { include_all_versions: e.target.checked, track_latest: e.target.checked ? false : r.track_latest })}
                        disabled={!r.project_id}
                        className="mt-0.5 rounded text-cs-accent focus:ring-cs-accent"
                        data-testid={`share-include-all-${i}`}
                      />
                      <span>
                        <span className="font-semibold">{t("share_include_all_label")}</span>
                        {" — "}
                        <span className="text-zinc-500">{t("share_include_all_help")}</span>
                      </span>
                    </label>
                    <label className="inline-flex items-start gap-1.5 text-[11px] text-zinc-700 cursor-pointer leading-tight">
                      <input
                        type="checkbox"
                        checked={!!r.track_latest}
                        onChange={(e) => setRow(i, { track_latest: e.target.checked })}
                        disabled={!r.project_id || r.include_all_versions}
                        className="mt-0.5 rounded text-cs-accent focus:ring-cs-accent"
                        data-testid={`share-track-latest-${i}`}
                      />
                      <span>
                        <span className="font-semibold">{t("share_track_latest_label")}</span>
                        {" — "}
                        <span className="text-zinc-500">{t("share_track_latest_help")}</span>
                      </span>
                    </label>
                  </div>
                  <div className="col-span-1 flex justify-end pt-1">
                    {rows.length > 1 && (
                      <button onClick={() => removeRow(i)} className="p-1.5 text-zinc-400 hover:text-red-600 rounded" title="Remover esta linha" data-testid={`share-row-remove-${i}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button onClick={addRow} className="inline-flex items-center gap-1 text-xs font-semibold text-cs-accent" data-testid="share-add-row"><Plus size={12} /> Adicionar</button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={allowIframe} onChange={(e) => setAllowIframe(e.target.checked)} className="rounded text-cs-accent focus:ring-cs-accent" data-testid="share-allow-iframe" />
              Permitir embed em iframe (Power BI)
            </label>
            {isEdit && (
              <label className="inline-flex items-center gap-2 text-xs">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="rounded text-cs-accent focus:ring-cs-accent" data-testid="share-enabled" />
                Link ativo
              </label>
            )}
          </div>
          <div className="border-t border-zinc-100 pt-4">
            <label className="cs-label block mb-1.5">Expiração (opcional)</label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={expiresAtInput}
                onChange={(e) => setExpiresAtInput(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="px-3 py-2 border border-zinc-200 rounded-md text-sm font-mono"
                data-testid="share-expires-input"
              />
              {expiresAtInput && (
                <button
                  type="button"
                  onClick={() => setExpiresAtInput("")}
                  className="text-[11px] text-zinc-500 hover:text-red-600 font-mono"
                  data-testid="share-expires-clear"
                >
                  remover expiração
                </button>
              )}
            </div>
            <p className="text-[11px] text-zinc-500 mt-1.5 font-mono">
              Após a data/hora informada o link retornará <code>410 Gone</code> e o iframe Power BI mostrará erro. Deixe vazio para nunca expirar.
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="share-cancel">Cancelar</button>
          <button onClick={onSubmit} disabled={saving} className="px-4 py-1.5 text-sm font-semibold bg-cs-accent text-white rounded-md hover:bg-cs-accentHover disabled:opacity-60" data-testid={isEdit ? "share-edit-submit" : "share-create-submit"}>{saving ? "..." : (isEdit ? "Salvar" : "Criar")}</button>
        </div>
      </div>
    </div>
  );
}
